import { useEffect, useState } from "react";
import { wfAPI } from "./wfApi";
import { toast } from "sonner";
import { Key, Plus, Clock, Wifi, Smartphone, Users, Globe, ShieldAlert } from "lucide-react";

const KIND_ICON = {
  schedule: Clock, network: Wifi, device_type: Smartphone, session: Users,
  geo: Globe, approval_gate: ShieldAlert, custom: Key,
};

export default function Policies() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ code: "", name: "", kind: "schedule", description: "", rules: "{}" });
  const [creating, setCreating] = useState(false);

  const load = () => wfAPI.policies().then(setItems).catch((e) => toast.error(e?.response?.data?.detail || "Failed"));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.code || !form.name) { toast.error("Code + name required"); return; }
    let parsedRules;
    try { parsedRules = JSON.parse(form.rules || "{}"); } catch { toast.error("Rules must be valid JSON"); return; }
    try {
      await wfAPI.createPolicy({ ...form, rules: parsedRules });
      setForm({ code:"", name:"", kind:"schedule", description:"", rules:"{}" });
      setCreating(false); load(); toast.success("Policy created");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="wf-policies">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-[18px] font-semibold text-hocp-navy">Access Policies</h2>
          <p className="text-[12.5px] text-slate-500">Reusable rule sets attachable to permission profiles and device policies.</p>
        </div>
        <button data-testid="create-policy-open" onClick={() => setCreating(true)}
          className="h-10 px-4 rounded-lg bg-hocp-gold text-hocp-navy text-[13px] font-medium inline-flex items-center gap-2">
          <Plus className="w-4 h-4"/> New Policy
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map((p) => {
          const Icon = KIND_ICON[p.kind] || Key;
          return (
            <div key={p.id} data-testid={`policy-${p.id}`} className="bg-white border border-slate-200 rounded-2xl p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center">
                  <Icon className="w-[16px] h-[16px] text-slate-600" strokeWidth={1.75}/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-hocp-navy truncate">{p.name}</div>
                  <div className="text-[11.5px] text-slate-400">{p.code}</div>
                </div>
                <span className="text-[10.5px] uppercase tracking-wider text-slate-400">{p.kind}</span>
              </div>
              <pre className="text-[11px] mono bg-slate-50 rounded-lg p-2.5 text-slate-600 max-h-[120px] overflow-y-auto">{JSON.stringify(p.rules || {}, null, 2)}</pre>
            </div>
          );
        })}
      </div>

      {creating && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setCreating(false)}>
          <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-100"><h3 className="font-display font-semibold text-hocp-navy">New Access Policy</h3></div>
            <div className="p-5 space-y-3">
              <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Code</span>
                <input placeholder="e.g. weekend_only" data-testid="new-policy-code" value={form.code} onChange={(e)=>setForm({...form, code:e.target.value})} className={inputCls}/></label>
              <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Name</span>
                <input placeholder="e.g. Weekend only access" data-testid="new-policy-name" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} className={inputCls}/></label>
              <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Kind</span>
                <select value={form.kind} onChange={(e)=>setForm({...form, kind:e.target.value})} className={inputCls}>
                  {Object.keys(KIND_ICON).map((k) => <option key={k} value={k}>{k}</option>)}
                </select></label>
              <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Rules (JSON)</span>
                <textarea placeholder='e.g. {"days":["mon","tue"],"from":"08:00","to":"20:00"}' rows={5}
                  value={form.rules} onChange={(e)=>setForm({...form, rules:e.target.value})}
                  data-testid="new-policy-rules"
                  className={inputCls + " mono"}/></label>
            </div>
            <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 flex justify-end gap-2">
              <button onClick={()=>setCreating(false)} className="h-9 px-3 rounded-lg text-slate-500 text-[13px] hover:bg-slate-100">Cancel</button>
              <button onClick={create} data-testid="new-policy-submit" className="h-9 px-4 rounded-lg bg-hocp-gold text-hocp-navy text-[13px] font-medium">Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
const inputCls = "w-full min-h-9 px-3 py-2 rounded-lg border border-slate-200 bg-white text-[13px] outline-none focus:border-hocp-navy transition";
